'use client'

import Image from 'next/image'
import { useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

export function TestimonialsSlider({ testimonials }) {
  const scrollContainerRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  // Get the width of a single card for navigation
  const getCardWidth = () => {
    const container = scrollContainerRef.current
    if (!container) return 400
    const firstCard = container.querySelector('[data-testimonial-card]')
    if (!firstCard) return 400
    return firstCard.offsetWidth + 24 // card width + gap
  }

  const scrollPrev = () => {
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollBy({ left: -getCardWidth(), behavior: 'smooth' })
  }

  const scrollNext = () => {
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollBy({ left: getCardWidth(), behavior: 'smooth' })
  }

  const highlightText = (text, highlight) => {
    if (!highlight) return text
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="font-semibold text-primary">
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const handleMouseDown = (e) => {
    setIsDragging(true)
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    const x = e.pageX - scrollContainerRef.current.offsetLeft
    const distance = (x - startX) * 2
    scrollContainerRef.current.scrollLeft = scrollLeft - distance
  }

  const handleTouchStart = (e) => {
    setIsDragging(true)
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft
    const distance = (x - startX) * 2
    scrollContainerRef.current.scrollLeft = scrollLeft - distance
  }

  return (
    <div className="py-6">
      <div
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleMouseUp}
        onTouchMove={handleTouchMove}
        className="flex overflow-x-scroll snap-x snap-mandatory gap-6 cursor-grab active:cursor-grabbing select-none scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {testimonials.map((testimonial, index) => (
          <div
            key={testimonial.author.name}
            data-testimonial-card
            className="bg-card border border-border p-6 md:p-8 min-w-full md:min-w-[400px] snap-start"
            data-testid={`testimonial-${index}`}
          >
            <div className="flex flex-col h-full">
              {/* Quote */}
              <blockquote className="flex-1 mb-6">
                <p className="text-base md:text-lg text-foreground font-serif leading-relaxed">
                  &ldquo;
                  {highlightText(testimonial.body, testimonial.highlight || '')}
                  &rdquo;
                </p>
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <Image
                  width={48}
                  height={48}
                  alt={testimonial.author.name}
                  src={testimonial.author.imageUrl}
                  className="h-12 w-12 rounded-full bg-muted object-cover"
                />
                <div>
                  <p className="font-sans text-sm font-semibold text-foreground">
                    {testimonial.author.name}
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    {testimonial.author.handle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Arrow Navigation */}
      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          onClick={scrollPrev}
          className="p-2 border border-border hover:bg-card transition-colors"
          aria-label="Previous testimonial"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <button
          onClick={scrollNext}
          className="p-2 border border-border hover:bg-card transition-colors"
          aria-label="Next testimonial"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
